import { Link } from "react-router-dom";
import { Shield, Sparkles, GraduationCap, Lock, Compass } from "lucide-react";

/**
 * TrustBadgeBand — homepage hero badges. Designed to feel calm and modern,
 * not corporate/fear-based. Five badges, all link to the relevant policy
 * page or the new /security-and-student-data hub so trust signals convert
 * to real document reads.
 *
 * Designed in pairs (icon + 2-word label) for fast skimmability. Educator
 * + parent friendly tone. No alarmist language.
 */
const BADGES = [
  {
    icon: <Shield className="w-4 h-4" />,
    label: "Student Data Privacy Focused",
    to: "/student-privacy",
    accent: "emerald",
    testId: "trust-badge-privacy",
  },
  {
    icon: <Sparkles className="w-4 h-4" />,
    label: "Responsible AI Learning",
    to: "/security-and-student-data#responsible-ai",
    accent: "sky",
    testId: "trust-badge-ai",
  },
  {
    icon: <GraduationCap className="w-4 h-4" />,
    label: "Educator Friendly",
    to: "/security-and-student-data",
    accent: "amber",
    testId: "trust-badge-educator",
  },
  {
    icon: <Lock className="w-4 h-4" />,
    label: "Secure Learning Environment",
    to: "/security-and-student-data#security",
    accent: "violet",
    testId: "trust-badge-secure",
  },
  {
    icon: <Compass className="w-4 h-4" />,
    label: "Future-Ready Learning",
    to: "/security-and-student-data#nevada",
    accent: "rose",
    testId: "trust-badge-future",
  },
];

const ACCENT_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100 hover:ring-emerald-300",
  sky: "bg-sky-50 text-sky-800 ring-sky-200 hover:bg-sky-100 hover:ring-sky-300",
  amber: "bg-amber-50 text-amber-800 ring-amber-200 hover:bg-amber-100 hover:ring-amber-300",
  violet: "bg-violet-50 text-violet-800 ring-violet-200 hover:bg-violet-100 hover:ring-violet-300",
  rose: "bg-rose-50 text-rose-800 ring-rose-200 hover:bg-rose-100 hover:ring-rose-300",
};

export default function TrustBadgeBand({ testId = "homepage-trust-band" }) {
  return (
    <div
      className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5"
      data-testid={testId}
      aria-label="Platform trust and safety highlights"
    >
      {BADGES.map((b) => (
        <Link
          key={b.label}
          to={b.to}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ring-1 text-xs sm:text-sm font-semibold transition-all ${ACCENT_CLASSES[b.accent]}`}
          data-testid={b.testId}
          title={`Learn more — ${b.label}`}
        >
          <span aria-hidden="true">{b.icon}</span>
          {b.label}
        </Link>
      ))}
    </div>
  );
}
